import { Button, Card, Divider, Typography } from 'antd';
import { useEffect, useState } from 'react';
import styles from './index.module.css';

import {
  Bar,
  BarChart,
  LabelList,
  ResponsiveContainer,
  XAxis,
  YAxis,
} from 'recharts';
import { logoBaseURL } from '../../../../Utils/constants';
import {
  convertAmoutToINRWithNotation,
  getLast6MonthsBalanceAccountWise,
} from '../../../../Utils/utils';
import { CashFlowProjection } from './CashFlowProjection';

type Props = {
  setCurrentMonth: (currentMonthYear: string) => void;
  setCurrentDataKey: (currentDataKey: string) => void;
  isFromCashFlow: boolean;
  setType: (type: string) => void;
};

const CashFlow = (props: Props) => {
  const [data, setData] = useState<any>([]);
  const [initalData, setInitialData] = useState<any>([]);
  const [loading, setLoading] = useState(true);
  const [currentMonthYear, setCurrentMonthYear] = useState<any>(`72024`);
  const [lastMonthsDetails, setLastMonthDetails] = useState<any>([]);
  const [currentDataKey, setCurrentDataKey] = useState<any>('totalDebitAmount');

  useEffect(() => {
    props.setType(currentDataKey);
  }, [currentDataKey]);

  useEffect(() => {
    props.setCurrentMonth(currentMonthYear);
  }, [currentMonthYear]);

  const filterDataMonthWise = (monthDetails: any) => {
    const currentMonthkey = `${monthDetails.month}${monthDetails.year}`;
    const newData = initalData.filter((element: any) => {
      if (`${element.month}${element.year}` === currentMonthkey) {
        return true;
      }
      return false;
    });
    setData(newData);
    setCurrentMonthYear(currentMonthkey);
  };
  const getIntialData = () => {
    const result = getLast6MonthsBalanceAccountWise();
    setInitialData(result.data);
    setData(
      result.data.filter((element: any) => {
        if (`${element.month}${element.year}` === currentMonthYear) {
          return true;
        }
        return false;
      })
    );
    setLastMonthDetails(result.Last6Months);
    setLoading(false);
  };

  const getCurrentMonthTotalAmount: any = () => {
    return {
      totalCreditAmount: data.reduce(
        (total: any, item: any) => total + item.totalCreditAmount,
        0
      ),
      totalDebitAmount: data.reduce(
        (total: any, item: any) => total + item.totalDebitAmount,
        0
      ),
    };
  };
  useEffect(() => {
    getIntialData();
  }, []);

  const renderCustomYAxisTick = ({ x, y, payload }: any) => {
    const linkedAccRef = payload.value;
    const item = data.find((entry: any) => entry.linkedAccRef === linkedAccRef); // Find the corresponding data entry
    const fipId = item && item.fipId; // Get the fipId if the item exists

    return (
      <g transform={`translate(${x},${y})`}>
        {fipId && (
          <image
            xlinkHref={`${logoBaseURL}${fipId}.jpeg`}
            width={20}
            height={20}
            x={-12 - 5}
            y={-10}
          />
        )}
      </g>
    );
  };
  return (
    <Card
      title={
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Typography.Text
            style={{
              fontSize: '16px',
              fontWeight: '500',
            }}
          >
            {props.isFromCashFlow ? 'Cash Flow' : 'Spend Analysis'}
          </Typography.Text>
          {!props.isFromCashFlow ? (
            <div style={{ display: 'flex', gap: '5px' }}>
              <Button
                className={`filter_button ${styles.filter_button}`}
                onClick={() => {
                  props.setCurrentDataKey('totalCreditAmount');
                  setCurrentDataKey('totalCreditAmount');
                }}
                type={
                  currentDataKey === 'totalCreditAmount' ? 'primary' : 'text'
                }
              >
                Credit
              </Button>
              <Button
                className={`filter_button ${styles.filter_button}`}
                onClick={() => {
                  props.setCurrentDataKey('totalDebitAmount');
                  setCurrentDataKey('totalDebitAmount');
                }}
                type={
                  currentDataKey === 'totalDebitAmount' ? 'primary' : 'text'
                }
              >
                Debit
              </Button>
            </div>
          ) : (
            <></>
          )}
        </div>
      }
      loading={loading}
      className={styles.cash_flow_card}
    >
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '8px',
          width: '100%',
        }}
      >
        {/* Cashflow Projection Chart */}
        {props.isFromCashFlow ? (
          <CashFlowProjection month={currentMonthYear.slice(0, -4)} />
        ) : (
          <></>
        )}
        {!props.isFromCashFlow ? (
          <Typography.Text className={styles.total_bank_balance_amount}>
            ₹
            {convertAmoutToINRWithNotation(
              getCurrentMonthTotalAmount()[currentDataKey]
            )}
          </Typography.Text>
        ) : (
          <></>
        )}
        {!props.isFromCashFlow ? (
          <ResponsiveContainer width='100%' minHeight={100}>
            <BarChart
              data={data}
              layout='vertical'
              barGap={0}
              margin={{ top: 0, right: 75, bottom: 0, left: -30 }} // Set left margin to
            >
              <XAxis type='number' hide />
              <YAxis
                type='category'
                dataKey='linkedAccRef'
                axisLine={false}
                tickLine={false}
                tick={renderCustomYAxisTick}
              />
              <Bar
                dataKey={currentDataKey}
                fill='#0460ff'
                barSize={24}
                radius={[4, 4, 4, 4]}
              >
                <LabelList
                  dataKey={currentDataKey}
                  position='right'
                  content={(value: any) => (
                    <g>
                      <text
                        x={value.x + value.width + 5} // Adjusted x position to the left of the bars
                        y={value.y + value.height / 2}
                        dominantBaseline='middle' // Vertically center the text
                        fill='var(--primary-color)' // Use CSS custom property for primary color
                        fontSize={14}
                        fontWeight={500}
                      >
                        ₹{convertAmoutToINRWithNotation(value.value)}
                      </text>
                    </g>
                  )}
                />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <></>
        )}
        <Divider style={{ margin: '12px' }} />
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
          }}
        >
          {lastMonthsDetails.map((element: any) => (
            <Button
              type={
                currentMonthYear === `${element.month}${element.year}`
                  ? 'primary'
                  : 'text'
              }
              onClick={() => filterDataMonthWise(element)}
              className={`month_button ${styles.month_button}`}
              style={{
                flexShrink: 1, // Allow buttons to shrink
                width: `${100 / lastMonthsDetails.length}%`,
              }}
            >
              {element.monthShortForm}
            </Button>
          ))}
        </div>
      </div>
    </Card>
  );
};

export default CashFlow;
